import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AuthService } from "../services/auth.service";
import { MainLayout } from "./MainLayout";
import { Card } from "./ui/card";
import { UserModel } from "../models/user.model";
import { DataService } from "../services/data.service";
import { getCurrentDate } from "../lib/utils";
import { ActivityReport } from "./ActivityReport";
import { ChartCard } from "./ChartCard";
import { MapPinHouse, Thermometer, Droplets, WindArrowDown, Lightbulb, Wind, Radar, ArrowLeft, Smile, History, ChartArea, BedDouble, Toilet, ThermometerSnowflake, Clock, Bath, Utensils, Tv, TreeDeciduous } from "lucide-react";
import { Beneficiary } from "./Beneficiary";
import { LocationReport } from "./LocationReport";
import { useLogout } from "../hooks/logout.hook";
import { PageTitle } from "./PageTitle";

export function DashboardDetails() {
    const navigate = useNavigate()
    const logout = useLogout()
    const { id } = useParams<{ id: any }>()
    const [user, setUser] = useState<UserModel>()
    const [userForDate, setUserForDate] = useState<UserModel>()
    const [globalDateOffset, setGlobalDateOffset] = useState<number>(0)

    useEffect(() => {
        if (!AuthService.hasAuth()) {
            navigate('/login', { replace: true });
            return
        }

        loadData()
        const interval = setInterval(() => loadData(true), 15 * 60 * 1000)
        return () => clearInterval(interval)
    }, [])

    async function loadData(silent = false) {
        try {
            const userRsp = await DataService.getUserById(id, silent)
            setUser(userRsp)

            // Load initial user for today
            if (globalDateOffset === 0)
                setUserForDate(userRsp)
        } catch (e: any) {
            logout()
        }
    }

    function backToDashboard() {
        navigate('/dashboard', { replace: true });
    }

    function goToDetailedReport(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
        e.preventDefault()
        const element = document.getElementById('activity_report');
        if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }

    const changeDate = (e: React.ChangeEvent<HTMLSelectElement>) => {
        e.preventDefault()
        setGlobalDateOffset(Number(e.target.value))

        // Load data for spefic date
        DataService.getUserById(id, true, getCurrentDate(Number(e.target.value), user?.time_zone))
            .then(rsp => setUserForDate(rsp))
            .catch(e => logout())
    }

    return (
        <MainLayout>
            <PageTitle>
                <ArrowLeft className="text-3xl text-gray-800 cursor-pointer" onClick={backToDashboard} size={32} />
                <h1 className="text-2xl font-bold text-gray-800 cursor-pointer" onClick={backToDashboard}>Dashboard Details</h1>
            </PageTitle>


            {user &&
                <div className="space-y-2">
                    <Beneficiary user={user} />

                    {user.settings.wellness_secore &&
                        <Card className="p-4 space-y-4 shadow-md rounded-lg border" id="score">
                            <div className="space-y-2">
                                <h3 className="text-lg font-semibold text-gray-800 flex items-center border-b pb-4">
                                    <Smile className="mr-2 text-gray-600 bg-gray-100 p-2 rounded-lg text-3xl" size={32} /> Wellness Score
                                </h3>
                                <p className="text-sm text-gray-500">
                                    Condition level: <span className={user.wellness_descriptor_color}>{user.wellness_descriptor}</span>
                                </p>
                                <div className="h-2 bg-gray-200 rounded-full">
                                    <div className="h-2 bg-green-500 rounded-full" style={{ width: `${user.wellness_score_percent}%` }}></div>
                                </div>
                                <div className="flex justify-center">
                                    <button className="w-2/4 py-2 text-sm font-medium text-white bg-blue-500 border border-blue-500 rounded-lg hover:bg-blue-600" type="button" onClick={e => goToDetailedReport(e)}>
                                        View detailed report
                                    </button>
                                </div>
                            </div>
                        </Card>
                    }

                    {user.settings.last_seen &&
                        <Card className="p-4 space-y-4 shadow-md rounded-lg border" id="room">
                            <div className="flex justify-between items-center border-b pb-4">
                                <div className="space-y-2">
                                    <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                                        <History className="mr-2 text-gray-600 bg-gray-100 p-2 rounded-lg text-3xl" size={32} /> Last Seen
                                    </h3>
                                </div>
                                <div className={`${user.last_seen_alert_colors} text-sm font-medium px-4 py-1 rounded-lg`}>
                                    {user.last_seen_alert}
                                </div>
                            </div>

                            <div className="space-y-2">
                                <div className="flex items-center bg-gray-100 p-2 rounded-lg space-x-3">
                                    <div className="bg-gray-100 p-2 rounded-lg">
                                        <MapPinHouse className="text-gray-800" />
                                    </div>

                                    <div className="flex flex-col">
                                        <p className="text-sm font-medium text-gray-800">{user.most_time_spent_in}</p>
                                        <span className="text-xs text-gray-500">Most time spent</span>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    }

                    <LocationReport
                        id={user.deployment_id}
                        globalDateOffset={globalDateOffset}
                        changeDate={changeDate}
                        timezone={user.time_zone}
                    />

                    {user.settings.sleep_report && userForDate &&
                        <Card className="p-4 space-y-4 shadow-md rounded-lg border" id="sleep_report">
                            <div className="space-y-2">
                                <div className="flex justify-between items-center border-b pb-2">
                                    <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                                        <ChartArea className="mr-2 text-gray-600 bg-gray-100 p-2 rounded-lg text-3xl" size={32} /> Sleep Report
                                    </h3>
                                    <select
                                        className="flex-grow w-full max-w-[45%] px-3 py-2 bg-gray-100 rounded-lg text-sm text-gray-800 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
                                        value={globalDateOffset}
                                        onChange={changeDate}
                                    >
                                        {Array.from({ length: 30 }, (_, i) => {
                                            const offset = -1 * i;
                                            return (
                                                <option key={i} value={offset}>
                                                    {offset === 0 ? 'Now' : getCurrentDate(offset, user.time_zone)}
                                                </option>
                                            );
                                        })}
                                    </select>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="flex flex-col p-2 bg-gray-100 rounded-lg">
                                        <div className="flex justify-between items-center">
                                            <p className="text-sm font-medium text-gray-800">{userForDate.sleep_hours} H</p>
                                            <BedDouble className="text-gray-800" />
                                        </div>
                                        <span className="text-xs text-gray-500">Sleep Hours</span>
                                    </div>
                                    <div className="flex flex-col p-2 bg-gray-100 rounded-lg">
                                        <div className="flex justify-between items-center">
                                            <p className="text-sm font-medium text-gray-800">{userForDate.sleep_bathroom_visit_count} times</p>
                                            <Toilet className="text-gray-800" />
                                        </div>
                                        <span className="text-xs text-gray-500">Bathroom Visits</span>
                                    </div>
                                    <div className="flex flex-col p-2 bg-gray-100 rounded-lg">
                                        <div className="flex justify-between items-center">
                                            <p className="text-sm font-medium text-gray-800">{Math.round(userForDate.bedroom_temperature)} {userForDate.units}</p>
                                            <ThermometerSnowflake className="text-gray-800" />
                                        </div>
                                        <span className="text-xs text-gray-500">Bedroom Temperature</span>
                                    </div>
                                    <div className="flex flex-col p-2 bg-gray-100 rounded-lg">
                                        <div className="flex justify-between items-center">
                                            <p className="text-sm font-medium text-gray-800">{userForDate.bedroom_co2} PPM</p>
                                            <Wind className="text-gray-800" />
                                        </div>
                                        <span className="text-xs text-gray-500">Room CO2</span>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    }

                    {user.settings.activity_report && <ActivityReport deploymentId={user.deployment_id} />}

                    {user.settings.radar &&
                        <ChartCard deploymentId={user.deployment_id} sensor={'radar'} timezone={user.time_zone} rooms={user.location_list}>
                            <Radar className="mr-2 text-gray-600 bg-gray-100 p-2 rounded-lg" size={32} /> Radar Data
                        </ChartCard>
                    }

                    {user.settings.light &&
                        <ChartCard deploymentId={user.deployment_id} sensor={'light'} timezone={user.time_zone} rooms={user.location_list}>
                            <Lightbulb className="mr-2 text-gray-600 bg-gray-100 p-2 rounded-lg" size={32} /> Room Light
                        </ChartCard>
                    }

                    {user.settings.air_quality &&
                        <ChartCard deploymentId={user.deployment_id} sensor={'co2'} timezone={user.time_zone} rooms={user.location_list}>
                            <Wind className="mr-2 text-gray-600 bg-gray-100 p-2 rounded-lg" size={32} /> Air Quality (CO<sub>2</sub>)
                        </ChartCard>
                    }

                    {user.settings.temperature &&
                        <ChartCard deploymentId={user.deployment_id} sensor={'temperature'} timezone={user.time_zone} rooms={user.location_list}>
                            <Thermometer className="mr-2 text-gray-600 bg-gray-100 p-2 rounded-lg" size={32} /> Temperature
                        </ChartCard>
                    }

                    {user.settings.humidity &&
                        <ChartCard deploymentId={user.deployment_id} sensor={'humidity'} timezone={user.time_zone} rooms={user.location_list}>
                            <Droplets className="mr-2 text-gray-600 bg-gray-100 p-2 rounded-lg" size={32} /> Humidity
                        </ChartCard>
                    }

                    {user.settings.air_pressure &&
                        <ChartCard deploymentId={user.deployment_id} sensor={'pressure'} timezone={user.time_zone} rooms={user.location_list}>
                            <WindArrowDown className="mr-2 text-gray-600 bg-gray-100 p-2 rounded-lg" size={32} /> Air Pressure
                        </ChartCard>
                    }

                    {user.settings.other_activities &&
                        <Card className="p-4 space-y-4 shadow-md rounded-lg border" id="other_activities">
                            <div className="space-y-2">
                                <h3 className="text-lg font-semibold text-gray-800 flex items-center border-b pb-4">
                                    <Clock className="mr-2 text-gray-600 bg-gray-100 p-2 rounded-lg text-3xl" size={32} />
                                </h3>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="flex flex-col p-2 bg-gray-100 rounded-lg">
                                        <div className="flex justify-between items-center">
                                            <p className="text-sm font-medium text-gray-800">{new Date(user.bathroom_at).toLocaleTimeString()}</p>
                                            <Bath className="text-gray-800" />
                                        </div>
                                        <span className="text-xs text-gray-500">Last Bathroom Visit</span>
                                    </div>
                                    <div className="flex flex-col p-2 bg-gray-100 rounded-lg">
                                        <div className="flex justify-between items-center">
                                            <p className="text-sm font-medium text-gray-800">{new Date(user.breakfast_detected_time).toLocaleTimeString()}</p>
                                            <Utensils className="text-gray-800" />
                                        </div>
                                        <span className="text-xs text-gray-500">Last Meal</span>
                                    </div>
                                    <div className="flex flex-col p-2 bg-gray-100 rounded-lg">
                                        <div className="flex justify-between items-center">
                                            <p className="text-sm font-medium text-gray-800">{user.living_room_time_spent} hours</p>
                                            <Tv className="text-gray-800" />
                                        </div>
                                        <span className="text-xs text-gray-500">Relaxing</span>
                                    </div>
                                    <div className="flex flex-col p-2 bg-gray-100 rounded-lg">
                                        <div className="flex justify-between items-center">
                                            <p className="text-sm font-medium text-gray-800">{user.outside_hours} hours</p>
                                            <TreeDeciduous className="text-gray-800" />
                                        </div>
                                        <span className="text-xs text-gray-500">Outside or Not Detected</span>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    }
                </div>
            }
        </MainLayout>
    );
}